/**
 * UploadStorageHealth — admin diagnostic tile (Model Management).
 *
 * Surfaces the result of `/api/admin/upload-storage-health`:
 *   • Green: every uploaded model file is reachable in object storage.
 *   • Amber: a few records are missing (≤ 5).
 *   • Red: many records are missing (> 5).
 *
 * Missing rows include the original model name + file name + reason so the
 * admin can ping the submitter for a re-upload. Does NOT mutate any record —
 * the admin still does the re-upload through the normal user flow.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, RefreshCw, XCircle, FileWarning } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  healthy: {
    border: 'border-emerald-300',
    bg: 'bg-emerald-50',
    icon: CheckCircle2,
    iconClass: 'text-emerald-600',
    badge: 'bg-emerald-100 text-emerald-700',
    label: 'All uploaded files available',
  },
  amber: {
    border: 'border-amber-300',
    bg: 'bg-amber-50',
    icon: AlertTriangle,
    iconClass: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-700',
    label: 'Some uploads missing',
  },
  red: {
    border: 'border-red-300',
    bg: 'bg-red-50',
    icon: XCircle,
    iconClass: 'text-red-600',
    badge: 'bg-red-100 text-red-700',
    label: 'Many uploads missing — investigate',
  },
};

export default function UploadStorageHealth({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHealth = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/admin/upload-storage-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(resp.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load upload storage health.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
  }, []); // intentional one-shot on mount

  const style = STATUS_STYLES[data?.overall_status || 'healthy'];
  const StatusIcon = style.icon;

  return (
    <Card
      className={`border-2 ${style.border} ${style.bg}`}
      data-testid="upload-storage-health-card"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <StatusIcon className={`h-5 w-5 ${style.iconClass}`} />
            <span>Upload Storage Health</span>
            <span
              className={`text-xs font-normal px-2 py-1 rounded-full ${style.badge}`}
              data-testid="upload-storage-health-badge"
            >
              {style.label}
            </span>
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchHealth}
            disabled={loading}
            data-testid="upload-storage-health-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Verifies that every uploaded SGR/FCR/temperature model file is still reachable in durable object storage. Re-upload required for any record marked missing — calculations, approval status, and other metadata are preserved.
        </p>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="bg-red-100 border border-red-300 rounded-md p-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {!error && data && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <Stat
                label="Total uploads"
                value={data.total_uploads}
                testId="storage-health-total"
              />
              <Stat
                label="Healthy"
                value={data.healthy_count}
                accent="text-emerald-700"
                testId="storage-health-healthy"
              />
              <Stat
                label="Missing"
                value={data.missing_count}
                accent={data.missing_count > 0 ? 'text-red-700' : 'text-emerald-700'}
                testId="storage-health-missing"
              />
            </div>

            {data.missing_count > 0 && (
              <div data-testid="upload-storage-health-missing-list">
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                  <FileWarning className="h-4 w-4 text-amber-600" />
                  File missing — re-upload required
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="text-left text-gray-600 border-b border-gray-200">
                        <th className="py-1.5 pr-2">Model name</th>
                        <th className="py-1.5 pr-2">Category</th>
                        <th className="py-1.5 pr-2">Uploader</th>
                        <th className="py-1.5 pr-2">Filename</th>
                        <th className="py-1.5 pr-2">Reason</th>
                        <th className="py-1.5 pr-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.missing.map((m) => (
                        <tr
                          key={m.id}
                          className="border-b border-gray-100"
                          data-testid={`storage-health-missing-row-${m.id}`}
                        >
                          <td className="py-1.5 pr-2 font-medium text-gray-800">{m.model_name || '—'}</td>
                          <td className="py-1.5 pr-2 text-gray-700">{m.category}</td>
                          <td className="py-1.5 pr-2 text-gray-700">{m.user_email || '—'}</td>
                          <td className="py-1.5 pr-2 text-gray-600">{m.file_name || '—'}</td>
                          <td className="py-1.5 pr-2 text-amber-700">
                            {m.missing_reason === 'legacy_local_disk_wiped' && 'Legacy upload (local disk wiped on restart)'}
                            {m.missing_reason === 'missing_storage_key' && 'No storage reference saved'}
                            {m.missing_reason === 'object_not_found' && 'Object not found in storage'}
                          </td>
                          <td className="py-1.5 pr-2 text-gray-700">{m.status || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, accent = 'text-gray-800', testId }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5">{label}</p>
      <p className={`text-2xl font-bold ${accent}`} data-testid={testId}>{value}</p>
    </div>
  );
}
